import type { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';

import {
  sanitizeLocQueryParam,
  sanitizeLocationPathParam,
} from '@/helpers/String/sanitizeQueryParams';
import { useGeoStore } from '@/stores/geo';

/** Thrown during SSR; Lambda maps this to HTTP 404. */
export const DIRECTORY_PAGE_NOT_FOUND = 'PAGE_NOT_FOUND';

/** Thrown when geo lookup failed transiently; Lambda maps this to HTTP 424. */
export const DIRECTORY_DEPENDENCY_FAILED = 'DEPENDENCY_FAILED_ERROR';

const BLOCKED_LOCATION_SLUGS = new Set(['null', 'undefined']);

const isGeoIdSegment = (segment: string): boolean => segment.startsWith('geo-');

const hasLocQueryParam = (locQuery: unknown): boolean =>
  (typeof locQuery === 'string' && locQuery !== '') ||
  (Array.isArray(locQuery) && locQuery.length > 0);

export const isDirectoryPageNotFoundError = (error: unknown): boolean =>
  error instanceof Error && error.message === DIRECTORY_PAGE_NOT_FOUND;

export const isDirectoryDependencyFailedError = (error: unknown): boolean =>
  error instanceof Error && error.message === DIRECTORY_DEPENDENCY_FAILED;

export const throwDirectoryPageNotFound = (): never => {
  throw new Error(DIRECTORY_PAGE_NOT_FOUND);
};

export const throwDirectoryDependencyFailed = (): never => {
  throw new Error(DIRECTORY_DEPENDENCY_FAILED);
};

const getRawLocParts = (queryVal: unknown): string[] => {
  if (Array.isArray(queryVal)) {
    return queryVal.flatMap(getRawLocParts);
  }

  if (typeof queryVal !== 'string') {
    return [];
  }

  return queryVal
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
};

/**
 * Builds requested location segments from route params for new Agency/Agent SRPs.
 * Throws {@link DIRECTORY_PAGE_NOT_FOUND} when the path or `loc` query is present but invalid.
 */
export const collectDirectoryRouteLocationSegments = (
  locationParam: unknown,
  locQuery: unknown,
): string[] => {
  const locations: string[] = [];

  if (locationParam !== undefined && locationParam !== null && String(locationParam) !== '') {
    if (typeof locationParam !== 'string') {
      throwDirectoryPageNotFound();
    }

    const pathLocation = sanitizeLocationPathParam(locationParam);
    if (pathLocation === undefined || BLOCKED_LOCATION_SLUGS.has(pathLocation)) {
      throw new Error(DIRECTORY_PAGE_NOT_FOUND);
    }

    locations.push(pathLocation);
  }

  if (hasLocQueryParam(locQuery)) {
    const fromQuery = sanitizeLocQueryParam(locQuery);
    const rawParts = getRawLocParts(locQuery);

    if (fromQuery.length === 0 || fromQuery.length !== rawParts.length) {
      throwDirectoryPageNotFound();
    }

    locations.push(...fromQuery);
  }

  return locations;
};

/**
 * When the route requested specific locations, every segment must resolve to a geo record.
 * Empty requests (Switzerland-wide) are allowed.
 */
export const assertDirectoryLocationsResolved = (
  requestedSegments: string[],
  resolvedLocations: GeoLocation[],
  locale: HgSupportedLanguage,
): void => {
  if (requestedSegments.length === 0) {
    return;
  }

  if (resolvedLocations.length === requestedSegments.length) {
    return;
  }

  const geoStore = useGeoStore();
  let hasUnresolvedLookup = false;

  for (const segment of requestedSegments) {
    const isResolved = isGeoIdSegment(segment)
      ? Boolean(geoStore.getGeoLocationById(segment))
      : Boolean(geoStore.getGeoLocationByUrlName(segment, locale));

    if (isResolved) {
      continue;
    }

    const isConfirmedMissing = isGeoIdSegment(segment)
      ? geoStore.isUnresolvableId(segment)
      : geoStore.isUnresolvableUrlName(segment, locale);

    if (!isConfirmedMissing) {
      hasUnresolvedLookup = true;
    }
  }

  if (hasUnresolvedLookup) {
    throwDirectoryDependencyFailed();
  }

  throwDirectoryPageNotFound();
};
